import { create } from 'zustand';
import api, { isGloballyHandled } from '../services/api';
import { leaveEmpathyTestSession } from '../services/empathyTestSession.service';
import { addFavorite, removeFavorite } from '../services/favorites.service';
import type {
  DataMode,
  GiverState,
  GiverStatus,
  QueueStats,
  SeekerState,
  SeekerStatus,
} from '../types';
import { forgetGiverQueueCall, noteGiverCall } from '../utils/giverQueueCall';
import { endLiveCall, onLiveCallEnded, resetLiveCall } from '../utils/liveCall';
import { clearElapsed, GIVER_WAIT_KEY } from '../utils/waitClock';
import { useIncomingCallStore } from './incoming-call.store';
import { onSessionChange, registerUserScopedReset, withSession } from './session-read';
import { getAxiosErrorMessage } from '../utils/error';
import { serverErrorMessage } from '../utils/extractApiError';

// ============================================
// Type Definitions
// ============================================

interface PreflightResults {
  camera_available: boolean;
  microphone_available: boolean;
  network_speed_kbps: number;
  browser_compatible: boolean;
}

interface EmpathyQueueState {
  // Giver state
  giverStatus: GiverStatus | null;
  giverState: GiverState;
  queuePosition: number | null;
  estimatedWaitMinutes: number | null;
  reliabilityScore: number | null;
  isLoadingGiver: boolean;
  giverError: string | null;

  // Seeker state
  seekerStatus: SeekerStatus | null;
  seekerState: SeekerState;
  rematchCount: number;
  fallbackActivity: string | null;
  isLoadingSeeker: boolean;
  seekerError: string | null;

  // System state
  dataMode: DataMode;

  // Room state
  roomUrl: string | null;
  roomToken: string | null;

  // Audio provider state
  audioProvider: 'p2p' | null;
  iceServers: Array<{ urls: string | string[]; username?: string; credential?: string }> | null;
  isInitiator: boolean;

  // Session state
  currentSessionId: string | null;
  /**
   * THE SERVER'S DEADLINE for the live session, ISO-8601 — `ends_at` off the
   * `empathy_sessions` row, identical for both roles. The client holds no cap
   * of its own; every countdown, phase and fallback is computed from this.
   *
   * The `sessionAnchor` below is its sibling at the other end of the same call:
   * that one says when this conversation STARTED, this one says when the server
   * will end it. Both come from the session and neither is the client's own.
   */
  sessionEndsAt: string | null;
  /**
   * HOW LONG THIS CALL MAY RUN IN TOTAL, in seconds — `ends_at - started_at`
   * as the SERVER armed it (`cap_seconds` off the same payload as the deadline
   * above), null until it arrives or on a backend older than the field.
   *
   * It is not the deadline and it is not the elapsed time: it is the SPAN, and
   * the shape of the ending is computed from it (`callCap.ts`). A three-minute
   * dev cap cannot say "wrapping up in five minutes" once the client knows how
   * long the call is.
   *
   * WHY THE SERVER SENDS IT rather than the client subtracting: the only start
   * the client has is `sessionAnchor`, stamped from ITS OWN clock when the
   * session first became current — so it restarts at zero on a reload and would
   * read a ninety-minute call as a short one (class C: two measurements of one
   * thing). The span belongs to the session, so it comes off the session.
   */
  sessionCapSeconds: number | null;
  /**
   * R12: until when the call screen waits for the partner to arrive, this
   * device's epoch ms: the server's `peer_wait_seconds` is what is LEFT of
   * its no-show window (from the session's start, #1000 review 7), turned
   * into a local deadline on receipt. Off the same payloads as
   * `sessionCapSeconds` and cleared with it. Null: not known.
   */
  sessionPeerWaitUntil: number | null;
  /**
   * WHEN THIS CALL STARTED, and WHICH call it was — one clock for the whole
   * app, keyed by the session it belongs to.
   *
   * The stage's elapsed timer and the queue pill's elapsed timer are the same
   * number said in two places, and two independent anchors is how they come to
   * disagree: the pill's would start when you navigated away and the stage's
   * would restart at 0:00 every time you navigated back.
   *
   * It is KEYED rather than cleared alongside `currentSessionId`, which is set
   * to null in four places and would have to grow a fifth line in each of them
   * (class E — a second copy of the truth that goes stale on the one path
   * somebody forgets). `sessionElapsedSeconds` refuses an anchor that does not
   * name the current session, so a leftover one reads as "no timer" instead of
   * as the wrong time.
   */
  sessionAnchor: { sessionId: string; startedAt: number } | null;
  partnerUserId: number | null;
  partnerName: string | null;
  /** Their face, for the tile and the post-session row. */
  partnerAvatarUrl: string | null;
  /** Their public address — `/u/{username}` — and the only one there is. */
  partnerUsername: string | null;
  /** A scripted practice partner. There is no person here to favourite. */
  partnerIsBot: boolean;
  /**
   * Whether YOU have favourited them, held here rather than in either of the
   * two components that show it: the heart on the call bar and the row on the
   * feedback screen are the same fact seconds apart, and two `useState`s is
   * how they start disagreeing.
   */
  partnerIsFavorited: boolean;
  partnerTranscriptConsent: boolean;
  lastCompletedSessionId: string | null;
  showFeedbackForm: boolean;
  feedbackRole: 'giver' | 'seeker' | null;

  /**
   * This seat is in an ADMIN 1:1 TEST SESSION, not in the queue.
   *
   * A test session is a real `empathy_sessions` row with real signalling and
   * the real PeerConnection — the only thing it is not is a MATCH. Nothing
   * created a `seekers` row for it, so the queue endpoint that owns this slice
   * knows nothing about it and must not be allowed to answer questions about
   * it (class C: the set the poll measures is not the set it would act on).
   */
  isTestSession: boolean;

  // Session end coordination
  endSessionRequested: boolean;
  isInPostSession: boolean;

  // Queue statistics
  queueStats: QueueStats | null;
  isLoadingStats: boolean;
  statsError: string | null;

  // Server-configured audio provider (from stats endpoint, available before session)
  configAudioProvider: 'p2p' | null;
}

interface EmpathyQueueActions {
  // Giver actions
  runPreflightCheck: (results: PreflightResults) => Promise<unknown>;
  joinGiverQueue: (force?: boolean) => Promise<unknown>;
  /**
   * @param endedSessionId the call this leave is the clean-up of (backlog
   * c-014). Given, the server leaves only if the seat has not moved on to
   * another call (a claim, ring or match that landed since) and otherwise
   * refuses; the refusal resolves to `EXIT_SUPERSEDED` and writes nothing
   * here. Omitted (the person's own Leave), whatever the seat holds is left.
   */
  leaveGiverQueue: (endedSessionId?: string | number | null) => Promise<unknown>;
  respondToPing: (response: 'ready' | 'not_yet' | 'timeout') => Promise<unknown>;
  fetchGiverStatus: () => Promise<unknown>;

  // Seeker actions
  runSeekerPreflightCheck: (results: PreflightResults) => Promise<unknown>;
  /**
   * @param capMinutes DEV/TEST ONLY — shorten this session's cap so a
   * ninety-minute ending can be watched in three. The server validates 1..90,
   * refuses it outright outside local/testing/staging, and clamps it again
   * before it reaches the session; there is no way to make a call LONGER.
   */
  requestEmpathy: (capMinutes?: number | null) => Promise<unknown>;
  /** @param endedSessionId as `leaveGiverQueue`'s: the call this cancel cleans up after. */
  cancelEmpathyRequest: (endedSessionId?: string | number | null) => Promise<unknown>;
  /**
   * #1000 review 4: "Not now" on the incoming-call sheet. Declines THAT
   * call only while no client of the seeker reached it or was handed it,
   * decided by the server under the session's lock. 'reached': the call is
   * live elsewhere and was left alone; 'ended': it is already over (or the
   * account changed). Throws on a transport failure (the sheet stays).
   */
  declineIncomingCall: (sessionId: number) => Promise<IncomingDeclineOutcome>;
  /**
   * Read the seat. `join`: R14, the incoming-call sheet's Join for that
   * session; the server presents it, so the seat stops saying awaiting_join.
   */
  fetchSeekerStatus: (opts?: { join?: number }) => Promise<unknown>;

  // Statistics actions
  fetchQueueStats: () => Promise<unknown>;

  // Utility actions
  setCurrentSession: (sessionId: string | number, role: 'giver' | 'seeker') => void;
  /**
   * Take the giver seat as a claim's own answer describes it: ACTIVE on that
   * session, with the person called. See the implementation.
   */
  adoptClaimedCall: (sessionId: string, partner: PartnerPayloadShape) => void;
  triggerFeedbackForm: (sessionId: string, role: 'giver' | 'seeker', delayMs?: number) => void;
  closeFeedbackForm: () => void;
  requestEndSession: () => void;
  clearEndSessionRequest: () => void;
  setIsInPostSession: (value: boolean) => void;
  enterTestSession: (session: TestSessionEntry) => void;
  /**
   * Add or remove the partner from your favourites. ONE function, reached by
   * the heart on the call bar and by the row on the feedback screen, so the
   * two can never be pointed at different code paths.
   */
  togglePartnerFavorite: () => Promise<void>;
  reset: () => void;
}

/**
 * Everything the production call UI needs, supplied BY THE SESSION.
 *
 * The test call used to be a second page that mounted `PeerAudioRoom` itself,
 * with its own header, its own Leave button and none of the rest — so the one
 * thing it could not test was what the call looks like. The way to render the
 * production UI is to give the production UI what a match gives it; this is
 * that, and it is why every field here is a field `fetchSeekerStatus` also
 * writes.
 */
export interface TestSessionEntry {
  sessionId: number;
  iceServers: Array<{ urls: string | string[]; username?: string; credential?: string }>;
  isInitiator: boolean;
  /**
   * THE SERVER'S PARTNER BLOCK, VERBATIM — not three fields picked out of it.
   *
   * It used to be `partnerUserId` / `partnerName` / `partnerAvatarUrl`, which
   * is how a test call ended up with a heart over a BOT: `partner_is_bot` and
   * `partner_username` were in the payload and simply not carried across, and
   * the hand-written copy had no way to say so. Handing the block through means
   * this entry point folds it with `partnerFrom` like every other one (class C)
   * and a field added to the payload arrives here for free.
   */
  partner: PartnerPayloadShape | null;
  /** The session's deadline, so a test call counts down like any other. */
  endsAt: string | null;
  /**
   * How long the call may run in total, seconds — the server's `cap_seconds`.
   *
   * REQUIRED rather than optional, for the same reason `callCapView` requires
   * it: a caller that does not pass it gets the ninety-minute shape on a
   * three-minute dev cap, and that is the bug the field exists to fix. The
   * compiler asks the question instead of answering it wrongly.
   */
  capSeconds: number | null;
}

type EmpathyQueueStore = EmpathyQueueState & EmpathyQueueActions;

// ============================================
// The partner, in one shape
// ============================================

/**
 * THE OTHER PERSON, AS ONE BLOCK — the client half of `App\Support\PartnerPayload`.
 *
 * Six places in this store used to set the partner fields one key at a time,
 * and they had already drifted: two cleared the avatar, one kept it; one
 * defaulted the name to 'Partner', the others to null. Adding a username and a
 * bot flag to six hand-written blocks is how a seventh gets written, so they
 * all assemble the block here instead.
 */
interface PartnerFields {
  partnerUserId: number | null;
  partnerName: string | null;
  partnerUsername: string | null;
  partnerAvatarUrl: string | null;
  partnerIsBot: boolean;
  partnerIsFavorited: boolean;
  partnerTranscriptConsent: boolean;
}

const NO_PARTNER: PartnerFields = {
  partnerUserId: null,
  partnerName: null,
  partnerUsername: null,
  partnerAvatarUrl: null,
  partnerIsBot: false,
  partnerIsFavorited: false,
  partnerTranscriptConsent: false,
};

export type IncomingDeclineOutcome = 'declined' | 'reached' | 'ended';

/** The seat after the seeker's call or search is over (Cancel and the sheet's decline). */
const SEEKER_SEAT_CLEARED = {
  isTestSession: false,
  seekerState: null,
  seekerStatus: null,
  rematchCount: 0,
  fallbackActivity: null,
  roomUrl: null,
  roomToken: null,
  audioProvider: null,
  iceServers: null,
  isInitiator: false,
  currentSessionId: null,
  // The deadline and the span are the SESSION's, not the partner's, so
  // they are cleared here beside `NO_PARTNER` rather than folded into
  // it: a call that is over has no clock, and one left behind would put
  // the next call's bar straight into "Ending now" (class E).
  sessionEndsAt: null,
  sessionCapSeconds: null,
  sessionPeerWaitUntil: null,
  ...NO_PARTNER,
  isLoadingSeeker: false,
} as const;

/** Seconds LEFT of the partner-arrival window (server-relative) → this device's deadline. */
function peerWaitUntil(secondsLeft: number | null | undefined): number | null {
  return typeof secondsLeft === 'number' ? Date.now() + Math.max(0, secondsLeft) * 1000 : null;
}

export interface PartnerPayloadShape {
  partner_user_id?: number | null;
  partner_name?: string | null;
  partner_username?: string | null;
  partner_avatar_url?: string | null;
  partner_is_bot?: boolean;
  partner_transcript_consent?: boolean;
}

/**
 * Fold a server payload's partner block into state.
 *
 * ONE RULE, AND IT IS ABOUT THE WHOLE PERSON RATHER THAN ABOUT FIELDS:
 *
 *   a payload that NAMES somebody replaces the record as a unit;
 *   a payload that names NOBODY says nothing, so the record stands as it is.
 *
 * `previous` is not a convenience. The status endpoints are POLLED every few
 * seconds, and the poll that ends the call carries `PartnerPayload::absent()`
 * — every field null, because server-side there is no ACTIVE session left to
 * read a partner off. That is the server saying "there is no current partner",
 * not "your partner has no face and is not a bot": the person is still on the
 * screen, the feedback row and the heart are about to be drawn for them, and
 * the record has to survive intact until something actually clears it.
 *
 * This was written field by field before, and the field-by-field version kept
 * the person HALF: the id, the name and the favourite survived the teardown
 * poll while the avatar and the bot flag were overwritten from the empty
 * block. An admin test call the bot ended therefore became "a real person you
 * may favourite" the moment it ended — the heart came alive, the
 * one-per-lifetime explainer was spent on it, and the server refused the
 * favourite (422) with nothing on screen having said why; ordinary partners
 * simply lost their face. An eighth field would have had to remember to join
 * the preserved list, which is how a ninth gets forgotten — so the rule is
 * about the record, not about the field (class D).
 *
 * WHY "NOTHING" RATHER THAN "PREVIOUS". The keep branch returns an EMPTY
 * object: every caller merges this straight into the state it read `previous`
 * from, so contributing no keys is precisely "leave the record alone" — and
 * unlike spreading `previous` back it cannot drag anything else along with it
 * (the polls hand this function their whole store state, stale `giverState`,
 * `roomUrl` and all).
 *
 * CLEARING IS SOMEBODY ELSE'S JOB, deliberately. `leaveGiverQueue`,
 * `cancelEmpathyRequest` and `reset` spread `NO_PARTNER` because they know the
 * call is over and no screen is still showing the person. A fold over a poll
 * never knows that.
 *
 * THE ONE FIELD THE SERVER DOES NOT SEND is `partnerIsFavorited`: it is ours,
 * so the replacing record carries it over when the arriving id is the same
 * partner and drops it when it is somebody new. Without the first half,
 * tapping the heart is undone by the next poll two seconds later. Without the
 * second, the next stranger inherits the last one's heart — class E, memory
 * disagreeing with the database about whose row this is.
 */
/**
 * R14: the seat of a claim not joined yet, as the waiting screen before it:
 * no session, no room, no clock, no partner. The server sends the real
 * values beside `awaiting_join` (an older client connects as it always did);
 * this client waits for Join.
 */
function preJoinSeat<T extends Record<string, unknown>>(seeker: T): T {
  return {
    ...seeker,
    state: seeker.waiting_state === 'MATCHING' ? 'MATCHING' : 'WAITING',
    session_id: null,
    room_url: null,
    room_token: null,
    ends_at: null,
    cap_seconds: null,
    peer_wait_seconds: null,
    partner_user_id: null,
  };
}

function partnerFrom(
  payload: PartnerPayloadShape | null | undefined,
  previous: PartnerFields,
  fallbackName: string | null = null,
): Partial<PartnerFields> {
  const userId = payload?.partner_user_id ?? null;

  // NOBODY NAMED — the teardown poll, a block the server omitted entirely, or
  // a live session whose partner's user row has gone. Say nothing.
  if (userId === null) return {};

  const samePartner = userId === previous.partnerUserId;

  return {
    partnerUserId: userId,
    partnerName: payload?.partner_name || fallbackName,
    partnerUsername: payload?.partner_username ?? null,
    partnerAvatarUrl: payload?.partner_avatar_url || null,
    partnerIsBot: payload?.partner_is_bot === true,
    partnerIsFavorited: samePartner ? previous.partnerIsFavorited : false,
    partnerTranscriptConsent: payload?.partner_transcript_consent || false,
  };
}

// ============================================
// The end of a call nobody was watching
// ============================================

/**
 * THE CALL IS OVER AND NO SCREEN SAID SO — the one place this store turns that
 * into the state the rest of the app reads.
 *
 * Two things can discover it, and both come here (class D). The backend can:
 * when the partner leaves, the session is completed server-side and the status
 * polls below — which the dashboard layout runs on EVERY page — come back with
 * the session gone. The connection can: `utils/liveCall.ts` follows it whether
 * or not a stage is attached, and announces a detached ending through
 * `onLiveCallEnded`.
 *
 * `endSessionRequested` is exactly the right flag rather than a new one. It
 * already means "this call is over, the flow has not run its teardown yet", so
 * `isCallSessionLive` goes false and the pill stops offering to return to a
 * call that has ended, and when the person next lands on the empathy page the
 * flow's existing watcher clears it and runs `handleEndCall` — which is how the
 * feedback screen is offered on return (class D again: no second ending path,
 * just the existing one reached from two more places).
 */
function releaseEndedSession(sessionId: string): void {
  // The hangup is addressed to a session the backend has already completed, so
  // this is the device release rather than a message: idempotent, and `false`
  // when there was no p2p call to release (a session that never was one).
  endLiveCall(Number(sessionId) || null);
  useEmpathyQueueStore.setState({ endSessionRequested: true });
}

/**
 * A DETACHED CALL ENDED BY ITSELF. Registered at module scope in the file that
 * owns the flag, so there is no wiring step for a caller to forget — and the
 * import goes one way only (this store imports `liveCall`, never the reverse).
 */
onLiveCallEnded(({ sessionId }) => {
  const { currentSessionId } = useEmpathyQueueStore.getState();
  // CLASS C — the set measured is the set acted on. A stale record's ending
  // must not flip the flag for a call the person is currently in.
  if (currentSessionId !== String(sessionId)) return;
  useEmpathyQueueStore.setState({ endSessionRequested: true });
});

// ============================================
// Store Implementation
// ============================================

/**
 * What a queue action resolves to when the session that issued it ended while
 * its request was out (a sign-out, or a switch to another account). Nothing
 * was written: the answer belongs to the previous account, and this store
 * already belongs to the next one (#940's rule, session-read.ts). Callers that
 * read fields off the result see none, so nothing acts on it either.
 */
export const SESSION_ENDED = Object.freeze({ status: 'session_ended' as const });

/**
 * WHAT A PINNED EXIT RESOLVES TO WHEN THE SEAT HAS MOVED ON (backlog c-014).
 *
 * An after-call clean-up (`leaveGiverQueue(id)` / `cancelEmpathyRequest(id)`)
 * names the call it ends. When a new claim, ring or match landed after that
 * call, the server refuses 409 `superseded` and changes nothing; this store
 * writes nothing either (the seat in memory is the new call's, or about to
 * be: the seat is read again at once), and the caller must not carry on as
 * if the seat were free (no Back, no rejoin, no "listen again").
 */
export const EXIT_SUPERSEDED = Object.freeze({ status: 'superseded' as const });

/**
 * THE JOIN'S "RECEIVE EMPATHY INSTEAD?" OFFER (backlog c-075).
 *
 * With a giver surplus the server answers join-queue 200 with
 * `suggest_switch` and seats nobody; `joinGiverQueue` hands that answer back
 * and writes no seat, so the seat stays OFFLINE. Every caller asks this one
 * reader whether the join seated them: `null` means it did not offer (it
 * seated them, or the session ended), an offer means it did NOT seat them and
 * the switch prompt is the answer to show.
 */
export interface SwitchSuggestion {
  givers_waiting: number;
  seekers_waiting: number;
  estimated_wait_minutes: number;
}

export function switchSuggestionOf(joinResult: unknown): SwitchSuggestion | null {
  if (!joinResult || typeof joinResult !== 'object') return null;
  const data = joinResult as {
    suggest_switch?: unknown;
    givers_waiting?: number;
    seekers_waiting?: number;
    estimated_wait_minutes?: number;
  };
  if (!data.suggest_switch) return null;
  return {
    givers_waiting: data.givers_waiting ?? 0,
    seekers_waiting: data.seekers_waiting ?? 0,
    estimated_wait_minutes: data.estimated_wait_minutes ?? 0,
  };
}

/** The pin the server reads, sent only when there is one (the person's own exit sends none). */
function exitPin(
  endedSessionId: string | number | null | undefined,
): { session_id: number } | null {
  if (endedSessionId === null || endedSessionId === undefined || endedSessionId === '') return null;
  const id = Number(endedSessionId);
  return Number.isFinite(id) && id > 0 ? { session_id: id } : null;
}

function isSupersededRefusal(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { error?: string } } })
    ?.response;
  return response?.status === 409 && response.data?.error === 'superseded';
}

/**
 * A queue entry the server refused AND explained (#1149 review, class B):
 * `{ success: false, error, message }`, the shape the ring's Accept and a
 * cohort claim answer with. `in_call` ("You're on a call right now.") is the
 * one join-queue and seeker/request send: the last call is still live on the
 * server. Thrown in place of the axios error, whose message is "Request
 * failed with status code 409", so the empathy page's ErrorModal and the
 * rejoin toast show the server's sentence. `code` is the server's `error`.
 */
export class QueueRefusalError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'QueueRefusalError';
    this.code = code;
  }
}

/** The explained refusals a queue entry can answer with (see QueueRefusalError). */
const EXPLAINED_QUEUE_REFUSALS: ReadonlySet<string> = new Set(['in_call']);

function explainedQueueRefusal(error: unknown): QueueRefusalError | null {
  if (isGloballyHandled(error) || !error || typeof error !== 'object') return null;
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== 'object') return null;
  const { success, error: code, message } = data as Record<string, unknown>;
  if (success !== false || typeof code !== 'string' || typeof message !== 'string') return null;
  if (!EXPLAINED_QUEUE_REFUSALS.has(code) || message === '') return null;
  return new QueueRefusalError(code, message);
}

/**
 * What a status poll resolves to when its answer is no longer about the seat
 * this store holds, and was therefore NOT written (see `pollTicket`). An
 * answer merely OVERTAKEN on the wire, about the seat still held, resolves
 * instead with the newer answer that was written (`droppedAnswer`). Like
 * `SESSION_ENDED` it carries no `giver` / `seeker` block, so a caller reading
 * fields off it sees none; a caller deciding "the call ended" from a missing
 * block must skip it first (`isStatusAnswer`).
 */
export const STALE_STATUS_ANSWER = Object.freeze({ status: 'stale_answer' as const });

/**
 * Is this what the SERVER said about the seat, rather than one of this store's
 * own "nothing was asked / nothing was written" results? Only a server answer
 * may be read as "the call is over" (class C: an answer about nothing, or about
 * a different session, is not evidence about this one). The admin test session
 * is one of them: the queue endpoint is never asked about it.
 */
export function isStatusAnswer(response: unknown): boolean {
  if (!response || typeof response !== 'object') return false;
  const status = (response as { status?: unknown }).status;
  return (
    status !== SESSION_ENDED.status &&
    status !== STALE_STATUS_ANSWER.status &&
    status !== 'test_session'
  );
}

// ============================================
// Which seat a status answer is ABOUT
// ============================================

/**
 * EVERY CHANGE OF `currentSessionId`, counted. Bumped by the subscription
 * under the store, so it sees every writer — `setCurrentSession`, the seat
 * clears, `reset` — without any of them having to remember to (class D).
 */
let sessionEpoch = 0;

/**
 * Per role: the last status request sent, the newest one whose answer was
 * written, and that answer (what an overtaken poll's caller decides from,
 * `droppedAnswer`).
 */
const pollSequence: Record<'giver' | 'seeker', { sent: number; applied: number; answer: unknown }> =
  {
    giver: { sent: 0, applied: 0, answer: null },
    seeker: { sent: 0, applied: 0, answer: null },
  };

interface PollTicket {
  role: 'giver' | 'seeker';
  seq: number;
  epoch: number;
  /** The session current when the request LEFT: the only one its answer can speak about. */
  sessionId: string | null;
  /** The role that session was adopted in (`setCurrentSession`); null when not known. */
  sessionRole: 'giver' | 'seeker' | null;
}

/**
 * THE SEAT A STATUS POLL IS ABOUT, taken when the request leaves.
 *
 * Staging, 2026-10-26: a seeker's poll left between sessions 65 and 66 and
 * landed after the store had adopted 66. Its answer ("no session") was read
 * against `currentSessionId` AFTER the await, i.e. against 66, and ended 66 —
 * the live call was torn down and a hangup was posted to it fifteen seconds in.
 * The set measured (the seat when the server read it) was not the set acted on
 * (the seat when the answer arrived): class C, and class E for the memory.
 */
/**
 * THE SESSION ID AS THIS STORE HOLDS IT: a string, whoever sent it. The status
 * reads and the request's match hand over `$activeSession->id`, a JSON
 * NUMBER; a claim, a ring's Accept and a test session hand over a string.
 * Held as it arrived, the two never compared equal: `onLiveCallEnded`'s
 * `currentSessionId !== String(sessionId)` refused every poll-adopted call,
 * so a call that died detached never set the pending End.
 */
function sessionKey(id: unknown): string | null {
  if (id === null || id === undefined || id === '') return null;
  return String(id);
}

function pollTicket(role: 'giver' | 'seeker'): PollTicket {
  const { currentSessionId, feedbackRole } = useEmpathyQueueStore.getState();
  return {
    role,
    seq: ++pollSequence[role].sent,
    epoch: sessionEpoch,
    sessionId: currentSessionId,
    sessionRole: currentSessionId ? feedbackRole : null,
  };
}

/**
 * An answer is STALE, and writes nothing, when:
 *
 *   - the current session changed while it was out (adopted, cleared, or
 *     replaced): it describes a seat this store no longer holds, so it can
 *     neither end the new session nor clear or overwrite its id, its clock or
 *     its room. The next poll asks about the seat as it is now;
 *   - a LATER request of the same role has already been written: an answer
 *     overtaken on the wire is older news than what is on screen.
 */
function isStale(ticket: PollTicket): boolean {
  return ticket.epoch !== sessionEpoch || ticket.seq < pollSequence[ticket.role].applied;
}

/**
 * WAS A SESSION ADOPTED WHILE A SEAT-WRITING REQUEST WAS OUT? The pollTicket
 * rule for the replies that are not status reads: `requestEmpathy`'s search,
 * `joinGiverQueue`'s seat and `respondToPing`'s answer. Each describes the
 * seat as the server held it BEFORE a match the store has since adopted (a
 * `ready` reply is sent after the server already matched inside the same
 * request, so the match event's poll can win the race), so it writes nothing:
 * written, it put the seat back to MATCHING / WAITING / PINGED under the live
 * call until the next poll (backlog c-096).
 *
 * Only an ADOPTION: a seat CLEARED meanwhile still takes the reply, or the
 * search or the queue seat the server just made would be left running with
 * nothing on screen saying so.
 */
function adoptedSince(epochAtSend: number): boolean {
  return epochAtSend !== sessionEpoch && useEmpathyQueueStore.getState().currentSessionId !== null;
}

/** This ticket's answer is the one now written: the newest for its role. */
function markApplied(ticket: PollTicket, answer: unknown): void {
  pollSequence[ticket.role].applied = ticket.seq;
  pollSequence[ticket.role].answer = answer;
}

/**
 * WHAT A STALE POLL RESOLVES TO: its own answer is dropped (it writes
 * nothing), and logged.
 *
 * Overtaken on the wire, about the seat still held (no session change since
 * it left): the NEWER answer that was written. A caller that decides from
 * the answer (the visibility handlers' "the call ended") must decide from the
 * newest news about this seat, not skip. c-073: the tab came back, a newer
 * poll answered "no seat" (the partner left, the row with it), which the
 * store does not end a call on; the visibility poll, overtaken, was skipped,
 * and nobody ended the dead call until the next poll plus the next return.
 *
 * The epochs prove the newer answer is about the same seat: it left after
 * this one, and no session change has happened since this one left.
 *
 * About a seat no longer held (the session changed while it was out):
 * `STALE_STATUS_ANSWER`, news about nothing current.
 */
function droppedAnswer(ticket: PollTicket): unknown {
  const sequence = pollSequence[ticket.role];
  const overtaken = ticket.epoch === sessionEpoch && sequence.answer !== null;
  console.info(
    `[empathy-queue] ${ticket.role} status answer #${ticket.seq} dropped: ` +
      (overtaken
        ? `overtaken by #${sequence.applied}, deciding from that one`
        : 'the session changed while it was out'),
  );
  return overtaken ? sequence.answer : STALE_STATUS_ANSWER;
}

/**
 * WHAT A STATUS POLL THAT FAILED ANSWERS: the c-073 rule for a poll whose own
 * request failed (a 5xx, a network change as the tab comes back). Overtaken on
 * the wire, about the seat still held (no session change since it left, and a
 * NEWER answer of its role written): it resolves with that newer answer, as an
 * overtaken poll that answered does (`droppedAnswer`). Its failure is older
 * news than what is on screen, and a caller deciding from the answer (the
 * visibility handlers' "the call ended") must decide from the newest news
 * about this seat, not log the failure and skip it (c-133: a newer poll said
 * "no seat", the visibility poll failed, and the dead call stayed up until the
 * next poll plus the next return).
 *
 * Anything else rethrows: a failure with nothing newer written is this seat's
 * failure, and one about a seat no longer held has no answer to hand back.
 */
function failedPollAnswer(ticket: PollTicket, error: unknown): unknown {
  const sequence = pollSequence[ticket.role];
  const overtaken =
    ticket.epoch === sessionEpoch && ticket.seq < sequence.applied && sequence.answer !== null;
  if (!overtaken) throw error;
  return droppedAnswer(ticket);
}

/**
 * Was the session this ticket left with adopted in THIS poll's role (or is
 * there no session, or no known role)? The giver's seat says nothing about a
 * session the same account holds as a seeker, and the reverse: both roles share
 * `currentSessionId` and the one room block (`roomUrl`, `roomToken`,
 * `audioProvider`, `iceServers`, `isInitiator`).
 */
function ticketOwnsSession(ticket: PollTicket): boolean {
  return ticket.sessionRole === null || ticket.sessionRole === ticket.role;
}

/**
 * Did the server just say the session this ticket is about is over? Only when
 * the answer names no session, the seat still reads in-call, and the session
 * was adopted in THIS role (`ticketOwnsSession`).
 */
function ticketSessionEnded(
  ticket: PollTicket,
  answer: { session_id?: unknown; state?: unknown },
  inCallState: string,
): ticket is PollTicket & { sessionId: string } {
  return (
    ticket.sessionId !== null &&
    ticketOwnsSession(ticket) &&
    !answer.session_id &&
    answer.state === inCallState
  );
}

export const useEmpathyQueueStore = create<EmpathyQueueStore>((set, get) => ({
  // Giver state
  giverStatus: null,
  giverState: 'OFFLINE',
  queuePosition: null,
  estimatedWaitMinutes: null,
  reliabilityScore: null,
  isLoadingGiver: false,
  giverError: null,

  // Seeker state
  seekerStatus: null,
  seekerState: null,
  rematchCount: 0,
  fallbackActivity: null,
  isLoadingSeeker: false,
  seekerError: null,

  // System state
  dataMode: null,

  // Room state
  roomUrl: null,
  roomToken: null,

  // Audio provider state (p2p vs daily)
  audioProvider: null,
  iceServers: null,
  isInitiator: false,

  // Session state
  currentSessionId: null,
  sessionEndsAt: null,
  sessionCapSeconds: null,
  sessionPeerWaitUntil: null,
  sessionAnchor: null,
  ...NO_PARTNER,
  lastCompletedSessionId: null,
  showFeedbackForm: false,
  feedbackRole: null,

  isTestSession: false,

  // Session end coordination
  endSessionRequested: false,
  isInPostSession: false,

  // Queue statistics
  queueStats: null,
  isLoadingStats: false,
  statsError: null,

  // Server-configured audio provider (from stats endpoint, available before session)
  configAudioProvider: null,

  // ============ GIVER ACTIONS ============

  runPreflightCheck: async (results: PreflightResults) => {
    set({ isLoadingGiver: true, giverError: null });

    try {
      const outcome = await withSession(() =>
        api.post('/empathy-queue/giver/preflight-check', results),
      );
      if (outcome.status === 'ended') return SESSION_ENDED;
      if (outcome.status === 'failed') throw outcome.error;
      const response = outcome.value;

      set({
        giverState: 'OFFLINE',
        isLoadingGiver: false,
      });

      return response.data;
    } catch (error) {
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage = serverErrorMessage(axiosError.response?.data) ?? 'Preflight check failed';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Preflight check failed');
      }
      set({ giverError: errorMessage, isLoadingGiver: false });
      throw error;
    }
  },

  joinGiverQueue: async (force = false) => {
    set({ isLoadingGiver: true, giverError: null });

    try {
      // requestEmpathy's rule (`adoptedSince`): a join answered after a poll
      // adopted a match must not put the live call back to WAITING.
      const epochAtSend = sessionEpoch;
      const outcome = await withSession(() =>
        api.post('/empathy-queue/giver/join-queue', { force }),
      );
      if (outcome.status === 'ended') return SESSION_ENDED;
      if (outcome.status === 'failed') throw outcome.error;
      const response = outcome.value;
      const data = response.data;

      // Before the switch offer too: an offer about the moment before a call
      // this store has since adopted is not one to show over that call (the
      // server answers `in_call` ahead of it for the same reason, b-202), so
      // the dropped reply hands back nothing a caller reads as an offer.
      if (adoptedSince(epochAtSend)) {
        set({ isLoadingGiver: false });
        return STALE_STATUS_ANSWER;
      }

      // Backend may suggest switching to seeker when giver surplus exists: not
      // seated, so no seat is written (callers read it: `switchSuggestionOf`).
      if (switchSuggestionOf(data)) {
        set({ isLoadingGiver: false });
        return data;
      }

      // A fresh wait (#992 F4): a stamp left by a wait this tab never saw
      // end (it ended after a reload, or in another tab) is not this one's.
      clearElapsed(GIVER_WAIT_KEY);
      set({
        giverState: data.auto_ready ? 'PINGED' : data.state || 'WAITING',
        queuePosition: data.queue_position,
        estimatedWaitMinutes: data.estimated_wait_minutes,
        isLoadingGiver: false,
      });

      // Immediately poll for status
      setTimeout(() => {
        void get().fetchGiverStatus();
      }, 500);

      return data;
    } catch (error) {
      const refusal = explainedQueueRefusal(error);
      if (refusal) {
        set({ giverError: refusal.message, isLoadingGiver: false });
        throw refusal;
      }
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage = serverErrorMessage(axiosError.response?.data) ?? 'Failed to join queue';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Failed to join queue');
      }
      // A refusal the upgrade prompt answered is not this store's line to show.
      set({ giverError: isGloballyHandled(error) ? null : errorMessage, isLoadingGiver: false });
      throw error;
    }
  },

  leaveGiverQueue: async (endedSessionId) => {
    set({ isLoadingGiver: true, giverError: null });

    try {
      const pin = exitPin(endedSessionId);
      const outcome = await withSession(() =>
        pin
          ? api.post('/empathy-queue/giver/leave-queue', pin)
          : api.post('/empathy-queue/giver/leave-queue'),
      );
      if (outcome.status === 'ended') return SESSION_ENDED;
      if (outcome.status === 'failed') {
        if (pin && isSupersededRefusal(outcome.error)) {
          // The seat is the new call's: nothing cleared, and it is read now
          // rather than at the next poll so the new call shows (class E).
          set({ isLoadingGiver: false });
          void get()
            .fetchGiverStatus()
            .catch(() => {});
          return EXIT_SUPERSEDED;
        }
        throw outcome.error;
      }
      const response = outcome.value;

      set({
        giverState: 'OFFLINE',
        queuePosition: null,
        estimatedWaitMinutes: null,
        roomUrl: null,
        roomToken: null,
        audioProvider: null,
        iceServers: null,
        isInitiator: false,
        currentSessionId: null,
        // The deadline and the span are the SESSION's, not the partner's, so
        // they are cleared here beside `NO_PARTNER` rather than folded into
        // it: a call that is over has no clock, and one left behind would put
        // the next call's bar straight into "Ending now" (class E).
        sessionEndsAt: null,
        sessionCapSeconds: null,
        sessionPeerWaitUntil: null,
        ...NO_PARTNER,
        isLoadingGiver: false,
      });

      return response.data;
    } catch (error) {
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage = serverErrorMessage(axiosError.response?.data) ?? 'Failed to leave queue';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Failed to leave queue');
      }
      set({ giverError: errorMessage, isLoadingGiver: false });
      throw error;
    }
  },

  respondToPing: async (pingResponse: 'ready' | 'not_yet' | 'timeout') => {
    set({ isLoadingGiver: true, giverError: null });

    try {
      // requestEmpathy's rule (`adoptedSince`), for every answer: a `ready`
      // reply lands after the match its own request made, and a poll that
      // adopted that match first must not be put back to PINGED (nor, for a
      // call adopted by another road, to WAITING / COOLDOWN / OFFLINE).
      const epochAtSend = sessionEpoch;
      const outcome = await withSession(() =>
        api.post('/empathy-queue/giver/ping-response', { response: pingResponse }),
      );
      if (outcome.status === 'ended') return SESSION_ENDED;
      if (outcome.status === 'failed') throw outcome.error;
      const res = outcome.value;
      const data = res.data;

      if (adoptedSince(epochAtSend)) {
        set({ isLoadingGiver: false });
      } else if (pingResponse === 'ready') {
        set({
          giverState: 'PINGED',
          isLoadingGiver: false,
        });
      } else if (pingResponse === 'timeout') {
        set({
          giverState: 'OFFLINE',
          queuePosition: null,
          isLoadingGiver: false,
        });
      } else {
        if (data.status === 'cooldown') {
          set({
            giverState: 'COOLDOWN',
            queuePosition: null,
            isLoadingGiver: false,
          });
        } else {
          set({
            giverState: 'WAITING',
            isLoadingGiver: false,
          });
        }
      }

      return data;
    } catch (error) {
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage = serverErrorMessage(axiosError.response?.data) ?? 'Failed to respond to ping';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Failed to respond to ping');
      }
      set({ giverError: errorMessage, isLoadingGiver: false });
      throw error;
    }
  },

  fetchGiverStatus: async () => {
    set({ isLoadingGiver: true, giverError: null });
    // Before the request leaves: the seat its answer is about (`pollTicket`).
    const ticket = pollTicket('giver');

    try {
      const outcome = await withSession(() => api.get('/empathy-queue/giver/status'));
      if (outcome.status === 'ended') return SESSION_ENDED;
      if (outcome.status === 'failed') throw outcome.error;
      if (isStale(ticket)) {
        set({ isLoadingGiver: false });
        return droppedAnswer(ticket);
      }
      const response = outcome.value;
      const data = response.data;
      markApplied(ticket, data);

      if (data.status === 'success' && data.giver) {
        const state = get();
        // The id as this store holds it: a string (`sessionKey`).
        const newSessionId = sessionKey(data.giver.session_id);
        // Not stale, so this is still the session the request left with.
        const oldSessionId = ticket.sessionId;

        // EVERY id this store adopts comes through `setCurrentSession`, which
        // is what stamps the clock. The guard was "no session yet", so a
        // second call that arrived without a null in between kept the FIRST
        // one's anchor — and an anchor naming another session is refused by
        // `sessionElapsedSeconds`, leaving the new call with no clock at all.
        // Comparing the ids also keeps the 15-second poll from re-stamping the
        // same session, which would pin the timer at 0:00 (class D).
        const adopted = newSessionId !== null && newSessionId !== oldSessionId;
        if (adopted) get().setCurrentSession(newSessionId, 'giver');
        // THE ROOM IS THE SESSION'S, and this seat's row speaks for it only
        // when the session is held as the GIVER (or this answer just adopted
        // one). A giver seat left ACTIVE after its own call, polled under a
        // session held as the seeker, rewrote `isInitiator` to `true` and the
        // room/ICE to its own row's (c-033). No mounted call read that copy:
        // each flow hands `PeerAudioRoom` its own `isInitiator`, and the room
        // keeps the ICE it mounted with. It was memory naming the wrong seat.
        const writesRoom = adopted || ticketOwnsSession(ticket);

        // Detect session ended: was ACTIVE with session, now backend returns no session
        // This happens when the other side leaves — backend marks session completed
        // but giver's state stays ACTIVE until they leave too.
        const sessionEnded = ticketSessionEnded(ticket, data.giver, 'ACTIVE');
        if (sessionEnded) releaseEndedSession(ticket.sessionId);

        set({
          giverStatus: data.giver,
          giverState: data.giver.state,
          queuePosition: data.giver.queue_position,
          estimatedWaitMinutes: data.giver.estimated_wait_minutes,
          reliabilityScore: data.giver.reliability_score,
          dataMode: data.giver.data_mode,
          ...(writesRoom
            ? {
                roomUrl: data.giver.room_url,
                roomToken: data.giver.room_token,
                audioProvider: data.giver.audio_provider || 'p2p',
                iceServers: data.giver.ice_servers || null,
                isInitiator: data.giver.is_initiator ?? true,
              }
            : {}),
          // If session ended on backend, clear the stale session ID
          currentSessionId: sessionEnded ? null : newSessionId || state.currentSessionId,
          // The deadline goes with the session id, always — a deadline left
          // behind from the last call would put the next one's bar straight
          // into "Ending now" (class E).
          sessionEndsAt: sessionEnded ? null : (data.giver.ends_at ?? state.sessionEndsAt),
          // The span travels with the deadline, always and everywhere — a cap
          // left behind from the last call would shape this one's ending
          // (class E, and the reason the test below counts the two names).
          sessionCapSeconds: sessionEnded
            ? null
            : (data.giver.cap_seconds ?? state.sessionCapSeconds),
          sessionPeerWaitUntil: sessionEnded
            ? null
            : (peerWaitUntil(data.giver.peer_wait_seconds) ?? state.sessionPeerWaitUntil),
          // THE CLOCK CLEARS AND THE PERSON DOES NOT, and that is deliberate.
          // The teardown poll carries no partner block, so `partnerFrom`
          // returns {} and the record stands — the feedback row and the heart
          // are about to be drawn for them. The deadline has no screen still
          // showing it, so it goes with the session id.
          ...partnerFrom(data.giver, state),
          isLoadingGiver: false,
        });
      } else {
        const state = get();
        set({
          giverStatus: null,
          giverState: state.currentSessionId ? state.giverState : 'OFFLINE',
          currentSessionId: state.currentSessionId,
          // No giver seat says nothing about a session held as the seeker
          // (`ticketOwnsSession`): its room stays (c-033, class D).
          ...(ticketOwnsSession(ticket) ? { roomUrl: null, roomToken: null } : {}),
          isLoadingGiver: false,
        });
      }

      return data;
    } catch (error) {
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage =
          serverErrorMessage(axiosError.response?.data) ?? 'Failed to fetch giver status';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Failed to fetch giver status');
      }
      // A failure of a request about a seat this store no longer holds is
      // not this seat's error to show (the caller still sees the throw).
      set({ giverError: isStale(ticket) ? get().giverError : errorMessage, isLoadingGiver: false });
      // Overtaken: the newer answer written, never a skip (c-133).
      return failedPollAnswer(ticket, error);
    }
  },

  // ============ SEEKER ACTIONS ============

  runSeekerPreflightCheck: async (results: PreflightResults) => {
    set({ isLoadingSeeker: true, seekerError: null });

    try {
      const outcome = await withSession(() =>
        api.post('/empathy-queue/seeker/preflight-check', results),
      );
      if (outcome.status === 'ended') return SESSION_ENDED;
      if (outcome.status === 'failed') throw outcome.error;
      const response = outcome.value;

      set({ isLoadingSeeker: false });

      return response.data;
    } catch (error) {
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage = serverErrorMessage(axiosError.response?.data) ?? 'Preflight check failed';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Preflight check failed');
      }
      set({ seekerError: errorMessage, isLoadingSeeker: false });
      throw error;
    }
  },

  requestEmpathy: async (capMinutes?: number | null) => {
    set({ isLoadingSeeker: true, seekerError: null });

    try {
      // The pollTicket rule for the one other answer that writes the seat: a
      // "searching" reply that lands after a status poll has already adopted
      // the match must not put the seat back to MATCHING under a live call.
      const epochAtSend = sessionEpoch;
      const outcome = await withSession(() =>
        api.post(
          '/empathy-queue/seeker/request',
          // Omitted entirely when there is no override: the endpoint refuses the
          // PARAMETER outside dev environments, so sending `cap_minutes: null`
          // from production would turn every request into a 403.
          capMinutes ? { cap_minutes: capMinutes } : {},
        ),
      );
      if (outcome.status === 'ended') return SESSION_ENDED;
      if (outcome.status === 'failed') throw outcome.error;
      const response = outcome.value;
      const data = response.data;

      if (data.status === 'matched') {
        // THROUGH THE ONE ACTION, not by assigning the id here. A seeker who
        // is matched on the spot never sees a `MATCHING` poll, and the polls
        // that follow skip `setCurrentSession` because an id is already
        // there — so a direct write here left `sessionAnchor` null for the
        // whole call: the pill showed `—` and the stage's clock restarted from
        // its mount fallback every time the person came back to it. One writer
        // for "this is the session now", and it stamps the clock (class D).
        get().setCurrentSession(data.session_id, 'seeker');
        set({
          seekerState: 'CONNECTED',
          roomUrl: data.room_url,
          roomToken: data.room_token,
          audioProvider: data.audio_provider || 'p2p',
          iceServers: data.ice_servers || null,
          isInitiator: data.is_initiator ?? false,
          // NOT `currentSessionId` — `setCurrentSession` above is the one
          // writer for that, and it is what stamps the anchor the clocks read.
          // The deadline and the span still belong here: they arrive with this
          // response and nothing else knows them.
          sessionEndsAt: data.ends_at ?? null,
          sessionCapSeconds: data.cap_seconds ?? null,
          sessionPeerWaitUntil: peerWaitUntil(data.peer_wait_seconds),
          ...partnerFrom(data, get(), 'Partner'),
          isLoadingSeeker: false,
        });
      } else if (adoptedSince(epochAtSend)) {
        // A match was adopted while this was out: the reply is older news
        // than the seat, so it writes nothing.
        set({ isLoadingSeeker: false });
      } else if (data.status === 'fallback_offered') {
        set({
          seekerState: 'FALLBACK_ACTIVITY',
          fallbackActivity: null,
          isLoadingSeeker: false,
        });
      } else {
        set({
          seekerState: 'MATCHING',
          isLoadingSeeker: false,
        });
      }

      return data;
    } catch (error) {
      const refusal = explainedQueueRefusal(error);
      if (refusal) {
        set({ seekerError: refusal.message, isLoadingSeeker: false });
        throw refusal;
      }
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage = serverErrorMessage(axiosError.response?.data) ?? 'Failed to request empathy';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Failed to request empathy');
      }
      set({ seekerError: isGloballyHandled(error) ? null : errorMessage, isLoadingSeeker: false });
      throw error;
    }
  },

  cancelEmpathyRequest: async (endedSessionId) => {
    set({ isLoadingSeeker: true, seekerError: null });

    try {
      // The same sibling as the poll above (class D): a test session has no
      // queue request to cancel, and `/seeker/cancel` would 422 on one while
      // the session — and the Node bot inside it — went on living until the
      // expiry sweep. Leaving is what ends it, and leaving is what the real
      // flow's Leave button ends up calling.
      const { isTestSession, currentSessionId } = get();

      // `leaveEmpathyTestSession`, not a raw POST: a 409 here is the NORMAL
      // ending, not a failure. `handleEndCall` sends `hangup` first and the
      // SignalingController turns that straight into a completed row, so by the
      // time the post-session flow closes, the session it is trying to leave is
      // already closed. A throw there would skip the reset below and leave the
      // seat in a dead call the hub would immediately re-render.
      // A test session has no queue to move on in: its leave takes no pin.
      const pin = isTestSession && currentSessionId ? null : exitPin(endedSessionId);
      const outcome = await withSession(async () =>
        isTestSession && currentSessionId
          ? { data: { outcome: await leaveEmpathyTestSession(Number(currentSessionId)) } }
          : pin
            ? await api.post('/empathy-queue/seeker/cancel', pin)
            : await api.post('/empathy-queue/seeker/cancel'),
      );
      if (outcome.status === 'ended') return SESSION_ENDED;
      if (outcome.status === 'failed') {
        if (pin && isSupersededRefusal(outcome.error)) {
          // leaveGiverQueue's sibling (class D): the seat is the new call's.
          set({ isLoadingSeeker: false });
          void get()
            .fetchSeekerStatus()
            .catch(() => {});
          return EXIT_SUPERSEDED;
        }
        throw outcome.error;
      }
      const response = outcome.value;

      set(SEEKER_SEAT_CLEARED);

      return response.data;
    } catch (error) {
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage = serverErrorMessage(axiosError.response?.data) ?? 'Failed to cancel request';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Failed to cancel request');
      }
      set({ seekerError: errorMessage, isLoadingSeeker: false });
      throw error;
    }
  },

  declineIncomingCall: async (sessionId) => {
    const outcome = await withSession(() =>
      api.post('/empathy-queue/seeker/cancel', { incoming_session_id: sessionId }),
    );
    if (outcome.status === 'ended') return 'ended';
    if (outcome.status === 'failed') {
      const response = (
        outcome.error as { response?: { status?: number; data?: { error?: string } } }
      )?.response;
      if (response?.status === 409 && response.data?.error === 'call_reached') return 'reached';
      if (response?.status === 409 && response.data?.error === 'call_ended') return 'ended';
      throw outcome.error;
    }
    // Declined: the call and the search it answered are over (Cancel's seat).
    set(SEEKER_SEAT_CLEARED);
    return 'declined';
  },

  fetchSeekerStatus: async (opts) => {
    // THE QUEUE IS NOT THE SOURCE OF TRUTH FOR A TEST SESSION.
    //
    // An admin test session deliberately creates no `seekers` row, so this
    // endpoint answers "no seeker" for one — truthfully, about a different
    // question. Letting that answer write this slice would tear down a live
    // call on the first poll. The seat's own state comes from
    // `/empathy/test-session/{id}`, which is the only endpoint that can see it.
    if (get().isTestSession) {
      return { status: 'test_session' };
    }

    set({ isLoadingSeeker: true, seekerError: null });
    // Before the request leaves: the seat its answer is about (`pollTicket`).
    const ticket = pollTicket('seeker');

    try {
      // #1000 review 5: a call screen that opens the call itself (SeekerFlow,
      // the group tab) says so, and the server keeps the incoming-call sheet
      // off every other tab and device for that session.
      const callScreen = useIncomingCallStore.getState().callScreens > 0;
      const params: Record<string, number> = {};
      if (callScreen) params.call_screen = 1;
      if (opts?.join) params.join = opts.join;
      const outcome = await withSession(() =>
        Object.keys(params).length > 0
          ? api.get('/empathy-queue/seeker/status', { params })
          : api.get('/empathy-queue/seeker/status'),
      );
      if (outcome.status === 'ended') return SESSION_ENDED;
      if (outcome.status === 'failed') throw outcome.error;
      if (isStale(ticket)) {
        set({ isLoadingSeeker: false });
        return droppedAnswer(ticket);
      }
      const response = outcome.value;
      const data = response.data;
      markApplied(ticket, data);

      if (data.status === 'success' && data.seeker) {
        // R14: a claim the person has not joined yet. Nothing here connects
        // it: the seat reads as the waiting screen it was (their group, or
        // everyone for a widened request) until the incoming-call sheet's
        // Join presents it. Every reader of this store (SeekerFlow, the
        // pill) then behaves as it did before the claim, by construction.
        if (data.seeker.awaiting_join === true) {
          data.seeker = preJoinSeat(data.seeker);
        }
        const state = get();
        // The id as this store holds it: a string (`sessionKey`).
        const newSessionId = sessionKey(data.seeker.session_id);
        // Not stale, so this is still the session the request left with.
        const oldSessionId = ticket.sessionId;

        // EVERY id this store adopts comes through `setCurrentSession`, which
        // is what stamps the clock. The guard was "no session yet", so a
        // second call that arrived without a null in between kept the FIRST
        // one's anchor — and an anchor naming another session is refused by
        // `sessionElapsedSeconds`, leaving the new call with no clock at all.
        // Comparing the ids also keeps the 15-second poll from re-stamping the
        // same session, which would pin the timer at 0:00 (class D).
        const adopted = newSessionId !== null && newSessionId !== oldSessionId;
        if (adopted) get().setCurrentSession(newSessionId, 'seeker');
        // The giver's sibling (c-033, class D): a seeker row speaks for the
        // room only when the session is held as the SEEKER, or it adopts one.
        const writesRoom = adopted || ticketOwnsSession(ticket);

        // Detect session ended: was CONNECTED with session, now backend returns no session
        const sessionEnded = ticketSessionEnded(ticket, data.seeker, 'CONNECTED');
        if (sessionEnded) releaseEndedSession(ticket.sessionId);

        set({
          seekerStatus: data.seeker,
          seekerState: data.seeker.state,
          rematchCount: data.seeker.rematch_count,
          fallbackActivity: data.seeker.fallback_activity,
          dataMode: data.seeker.data_mode,
          ...(writesRoom
            ? {
                roomUrl: data.seeker.room_url,
                roomToken: data.seeker.room_token,
                audioProvider: data.seeker.audio_provider || 'p2p',
                iceServers: data.seeker.ice_servers || null,
                isInitiator: data.seeker.is_initiator ?? false,
              }
            : {}),
          currentSessionId: sessionEnded ? null : newSessionId || state.currentSessionId,
          // The giver's sibling (class D) — same rule, same column.
          sessionEndsAt: sessionEnded ? null : (data.seeker.ends_at ?? state.sessionEndsAt),
          sessionCapSeconds: sessionEnded
            ? null
            : (data.seeker.cap_seconds ?? state.sessionCapSeconds),
          sessionPeerWaitUntil: sessionEnded
            ? null
            : (peerWaitUntil(data.seeker.peer_wait_seconds) ?? state.sessionPeerWaitUntil),
          ...partnerFrom(data.seeker, state),
          isLoadingSeeker: false,
        });
      } else {
        const state = get();
        set({
          seekerStatus: null,
          seekerState: state.currentSessionId ? state.seekerState : null,
          currentSessionId: state.currentSessionId,
          isLoadingSeeker: false,
        });
      }

      return data;
    } catch (error) {
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage =
          serverErrorMessage(axiosError.response?.data) ?? 'Failed to fetch seeker status';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Failed to fetch seeker status');
      }
      // A failure of a request about a seat this store no longer holds is
      // not this seat's error to show (the caller still sees the throw).
      set({
        seekerError: isStale(ticket) ? get().seekerError : errorMessage,
        isLoadingSeeker: false,
      });
      // Overtaken: the newer answer written, never a skip (c-133).
      return failedPollAnswer(ticket, error);
    }
  },

  // ============ STATISTICS ACTIONS ============

  fetchQueueStats: async () => {
    set({ isLoadingStats: true, statsError: null });

    try {
      const response = await api.get('/empathy-queue/stats');

      const stats = response.data.stats;
      set({
        queueStats: stats,
        isLoadingStats: false,
        // Extract server-configured audio provider for preflight checks
        ...(stats?.audio_provider ? { configAudioProvider: stats.audio_provider } : {}),
      });

      return response.data;
    } catch (error) {
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        errorMessage = serverErrorMessage(axiosError.response?.data) ?? 'Failed to fetch stats';
      } else if (error instanceof Error) {
        errorMessage = getAxiosErrorMessage(error, 'Failed to fetch stats');
      }
      set({ statsError: errorMessage, isLoadingStats: false });
      throw error;
    }
  },

  // ============ UTILITY ACTIONS ============

  setCurrentSession: (rawSessionId: string | number, role: 'giver' | 'seeker') => {
    const sessionId = String(rawSessionId);
    // Which road the listener took to this call, read off the seat BEFORE the
    // call's own state lands (every writer adopts the id first): the
    // after-call path rejoins the queue only for a call the queue made.
    if (role === 'giver') noteGiverCall(sessionId, get().giverState);
    // A PENDING END IS THE LAST CALL'S. A call that ended while no flow was
    // mounted leaves `endSessionRequested` set for the flow to run on return
    // (`releaseEndedSession`). If another call is adopted first (a ring's
    // Accept, a booked call's Join, the incoming call's Join), the flow that
    // mounts for the NEW call ran that End as its own: it left the queue,
    // which completed the new call on the server, or rejoined it under the
    // live call. The anchor still names the last call after the poll clears
    // its id, so "another call" is read against it; the same call read again
    // keeps its own End.
    //
    // So is the last call's FEEDBACK FLAG (backlog c-074). A listener who left
    // the empathy page while that call's feedback screen was up unmounted the
    // flow, and only the mounted flow clears `isInPostSession`
    // (`useNewCallSupersedes`); a claim, a ring or a status read then carried
    // it into the new call, `isCallSessionLive` read false and the pill did
    // not offer the call. The flag is about the call the feedback was for, so
    // it goes when ANOTHER call is adopted, here, where every way into a call
    // passes (class D); the finished call read again keeps it.
    const anotherCall = get().sessionAnchor?.sessionId !== sessionId;
    set({
      currentSessionId: sessionId,
      feedbackRole: role,
      // The one anchor every elapsed clock in this app reads. Set HERE because
      // this is the single point at which a session becomes the current one —
      // both status polls funnel through it.
      sessionAnchor: { sessionId, startedAt: Date.now() },
      ...(anotherCall ? { endSessionRequested: false, isInPostSession: false } : {}),
    });
  },

  /**
   * THE CLAIM'S ANSWER IS THE SEAT, until the seat is read (R14).
   *
   * A claim from a group card (POST empathy/requests/{id}/claim) answers with
   * the session it built, and the server has put this giver's row ACTIVE on
   * it. The 1:1 page used to learn that only from its own first
   * giver/status read. When the person called said Not now before that read
   * landed (a slow phone; the cloud's failed R14, run 40845095222), the read
   * answered OFFLINE with no session, so the page had no call to end: no
   * "can't talk right now", no signal poll to hear the server's `end`, just
   * "Start a call" as if nothing had happened (class E: memory said no call
   * while the database had one, then ended it).
   *
   * So the seat in memory is the claim's answer from the moment it arrives:
   * ACTIVE on that session, partner from the card. The first status read
   * then confirms it, or (OFFLINE, no session) ends it through GiverFlow like
   * any other ending, and `callEndRoute` says what that ending was. Whatever
   * the last call left (connection data, deadline, span) is not this call's
   * and is cleared; the status read brings this call's own.
   *
   * So is the last call's pending End (`endSessionRequested`). A call the
   * partner ended while no flow was mounted leaves it set for the flow to run
   * on return (`releaseEndedSession`), and the server lets that listener
   * claim. Kept, the flow that mounts for THIS call ran it as this person's
   * own End: it left the queue and dropped the call they had just claimed.
   *
   * ALL OF THAT IS FOR A CALL THIS STORE DOES NOT HOLD YET (backlog c-076).
   * The giver status read prompted by the accept's own
   * `empathy.status.changed` can land BEFORE this answer, and then it has
   * already adopted the session with its own ICE servers, room, deadline and
   * clock. Clearing those wiped live connection data: GiverFlow's
   * `audioReady` went false, the stage unmounted until the next read, and the
   * re-stamped anchor restarted the call clock. The session already held is
   * the read's to describe; the claim adds who is on the other end, and that
   * the seat is ACTIVE. That one the answer is the authority on: the server
   * creates the active session before the transaction that writes the giver
   * row ACTIVE (`finalizeMatch`; a ring Accept and a claim both reserve the
   * row PINGED first), so a read served in between adopts the session as
   * PINGED, and without this the 1:1 page showed "You're in the queue" over
   * the live call until the next read.
   */
  adoptClaimedCall: (sessionId: string, partner: PartnerPayloadShape) => {
    if (get().currentSessionId === sessionId) {
      set((state) => ({ giverState: 'ACTIVE', ...partnerFrom(partner, state) }));
      return;
    }
    get().setCurrentSession(sessionId, 'giver');
    set((state) => ({
      giverState: 'ACTIVE',
      iceServers: null,
      roomUrl: null,
      roomToken: null,
      sessionEndsAt: null,
      sessionCapSeconds: null,
      sessionPeerWaitUntil: null,
      endSessionRequested: false,
      ...partnerFrom(partner, state),
    }));
  },

  triggerFeedbackForm: (sessionId: string, role: 'giver' | 'seeker', delayMs = 120000) => {
    // The feedback is about THIS account's call. The wait is session-bound
    // work like any other (withSession): if the session has changed by the
    // time the delay is up, the form would ask the next account (#940).
    void withSession(() => new Promise<void>((done) => setTimeout(done, delayMs))).then(
      (outcome) => {
        if (outcome.status !== 'ok') return;
        const state = get();
        if (state.giverState === 'OFFLINE' && state.seekerState === null) {
          set({
            lastCompletedSessionId: sessionId,
            feedbackRole: role,
            showFeedbackForm: true,
          });
        }
      },
    );
  },

  closeFeedbackForm: () => {
    set({ showFeedbackForm: false });
  },

  requestEndSession: () => {
    set({ endSessionRequested: true });
  },

  clearEndSessionRequest: () => {
    set({ endSessionRequested: false });
  },

  setIsInPostSession: (value: boolean) => {
    set({ isInPostSession: value });
  },

  /**
   * Seat this account in an admin test session, in the state a MATCH leaves it.
   *
   * Every field written here is one `fetchSeekerStatus` writes for a real
   * match, and nothing else is written at all — that is the whole point.
   * `/app/empathy` then renders `SeekerFlow` -> CONNECTED -> `PeerAudioRoom`
   * with no idea it is a test, because there is nothing about it to know.
   *
   * THROUGH `setCurrentSession`, LIKE EVERY OTHER MATCH. This wrote
   * `currentSessionId`, `sessionAnchor` and `feedbackRole` by hand — the three
   * fields that action exists to write together — which made it a THIRD writer
   * of the anchor beside the two status polls, and the one that would be
   * forgotten the next time the anchor grows a field. It is the same class-D
   * defect `requestEmpathy` was fixed for in #891, arriving from a branch that
   * was cut before that fix (class D: one writer for "this is the session
   * now").
   */
  enterTestSession: (session: TestSessionEntry) => {
    // First, because it is what stamps the clock the bar and the pill read.
    get().setCurrentSession(String(session.sessionId), 'seeker');
    set({
      isTestSession: true,
      seekerState: 'CONNECTED',
      seekerStatus: null,
      seekerError: null,
      isLoadingSeeker: false,
      rematchCount: 0,
      fallbackActivity: null,
      roomUrl: null,
      roomToken: null,
      audioProvider: 'p2p',
      iceServers: session.iceServers,
      isInitiator: session.isInitiator,
      sessionEndsAt: session.endsAt,
      // THE DEADLINE AND THE SPAN TRAVEL TOGETHER, here as in every other
      // entry point (class D). A test call is normally armed with the
      // three-minute dev override, so a null span here is not a cosmetic
      // default: it is the ninety-minute shape on a three-minute call, which
      // is the whole reason the server sends this number.
      sessionCapSeconds: session.capSeconds,
      // An admin test call's bot answers at once: no wait window (R12).
      sessionPeerWaitUntil: null,
      // THE ONE FOLD, from the empty state — never from `get()`. A test call is
      // a fresh partner by definition, and folding against whoever was here
      // last is how the previous person's heart would arrive filled.
      ...partnerFrom(session.partner, NO_PARTNER),
      endSessionRequested: false,
      isInPostSession: false,
    });
  },

  /**
   * FAVOURITE THE PERSON YOU JUST TALKED TO.
   *
   * Optimistic, because the heart is a one-tap gesture in the middle of a live
   * call and a spinner there reads as a failure. Reverted on refusal, because
   * a heart that stays filled over a request the server rejected is a lie the
   * user has no way to detect — class B, the refusal reaches the caller.
   *
   * It refuses a bot ITSELF rather than relying on the screens to hide the
   * control: `FavoritesController::store` answers 422 for one, and a client
   * that asked anyway would have flipped the heart on the way out and flipped
   * it back a moment later for no reason the user can see.
   */
  togglePartnerFavorite: async () => {
    const { partnerUserId, partnerIsBot, partnerIsFavorited } = get();
    if (partnerUserId === null || partnerIsBot) return;

    const previous = partnerIsFavorited;
    set({ partnerIsFavorited: !previous });

    const result = previous
      ? await removeFavorite(partnerUserId)
      : await addFavorite(partnerUserId);

    if (!result.success) {
      // Only OUR optimistic flip is undone. A match that landed while the
      // request was in flight has already reset the flag for a different
      // person, and putting the old value back would be about the wrong one.
      if (get().partnerUserId === partnerUserId) {
        set({ partnerIsFavorited: previous });
      }
    }
  },

  reset: () => {
    // A reset is a sign-out or a hard teardown, and a `PeerConnectionManager`
    // does not go away because a store forgot about it. No hangup: there is no
    // longer a session to address one to (class D — every other end funnels
    // through the flows' `handleEndCall`, and this is the one that does not).
    resetLiveCall();
    forgetGiverQueueCall();
    // An answer about the seat before the reset is no one's to decide from.
    pollSequence.giver.answer = null;
    pollSequence.seeker.answer = null;
    set({
      giverStatus: null,
      giverState: 'OFFLINE',
      queuePosition: null,
      estimatedWaitMinutes: null,
      reliabilityScore: null,
      isLoadingGiver: false,
      giverError: null,
      seekerStatus: null,
      seekerState: null,
      rematchCount: 0,
      fallbackActivity: null,
      isLoadingSeeker: false,
      seekerError: null,
      roomUrl: null,
      roomToken: null,
      audioProvider: null,
      iceServers: null,
      isInitiator: false,
      currentSessionId: null,
      sessionEndsAt: null,
      sessionCapSeconds: null,
      sessionPeerWaitUntil: null,
      sessionAnchor: null,
      ...NO_PARTNER,
      lastCompletedSessionId: null,
      showFeedbackForm: false,
      feedbackRole: null,
      endSessionRequested: false,
      isInPostSession: false,
      isTestSession: false,
    });
  },
}));

/**
 * #992 F4: every exit from the wait (left from anywhere, matched, expired,
 * a poll that finds the row gone) forgets the giver's wait clock HERE, at the
 * one place every path writes, not in a component that may not be mounted —
 * the leave that unmounts GiverFlow in the same render included (Codex #992).
 */
const WAITING_GIVER: ReadonlySet<GiverState> = new Set<GiverState>(['WAITING', 'PINGED']);
useEmpathyQueueStore.subscribe((state, prev) => {
  if (WAITING_GIVER.has(prev.giverState) && !WAITING_GIVER.has(state.giverState)) {
    clearElapsed(GIVER_WAIT_KEY);
  }
  // Every change of the current session, from whichever writer: an answer
  // that left before it is about a seat this store no longer holds.
  if (state.currentSessionId !== prev.currentSessionId) sessionEpoch++;
});

/**
 * HOW LONG THIS CALL HAS BEEN RUNNING — the one answer, for the stage's clock
 * and the queue pill's alike.
 *
 * Null when nothing anchors it: no session, or an anchor left over from a
 * previous one. A wrong number on a call timer is worse than no number, so the
 * mismatch degrades to silence rather than to a guess.
 */
export function sessionElapsedSeconds(
  state: Pick<EmpathyQueueState, 'currentSessionId' | 'sessionAnchor'>,
  now: number = Date.now(),
): number | null {
  const { currentSessionId, sessionAnchor } = state;
  if (!currentSessionId || !sessionAnchor || sessionAnchor.sessionId !== currentSessionId) {
    return null;
  }
  // Clamped for the reason the room's own timer clamps: a clock adjusted
  // backwards mid-call (NTP, a manual change) must not read as negative.
  return Math.max(0, Math.floor((now - sessionAnchor.startedAt) / 1000));
}

/**
 * IS THERE A CALL YOU CAN STILL GO BACK TO?
 *
 * THE ONE PREDICATE, and the reason it is one. "The queue says you are in a
 * call" and "there is a live call to return to" are different questions, and
 * on 2026-09-14 the floating pill answered the second with the first: after a
 * call ended, the pill sat over the feedback screen still saying "In a call"
 * with a "Return to call" button, because `giverState` genuinely still reads
 * `ACTIVE` while the feedback is up — `leaveGiverQueue` is deliberately
 * deferred until the post-session flow closes.
 *
 * So the phase matters as much as the state, and three flags carry it:
 *
 *   `endSessionRequested` — somebody pressed End. The flow has not run the
 *      teardown yet, but the answer is already no.
 *   `isInPostSession`     — the feedback / tip / completion flow is on screen.
 *   `showFeedbackForm`    — the delayed feedback form, which comes back
 *      minutes later for a session that ended long ago.
 *
 * WHO ASKS. The pill, to decide whether to render at all and whether its state
 * is `in-call`; and the Back handler, to decide whether there is a stage worth
 * minimising or whether Back is ordinary history back. They must not each keep
 * their own copy of that list — the one that is not updated is the one that
 * goes on covering a feedback screen (class D).
 *
 * The giver/seeker states are taken from the ARGUMENT rather than read off the
 * store inside, because the pill renders from props handed down by the
 * dashboard layout: the set this predicate measures has to be the set the
 * caller acts on (class C).
 */
export interface CallSessionFacts {
  /** Optional, because the pill is handed these as props and may have neither. */
  giverState?: GiverState;
  seekerState?: SeekerState;
  endSessionRequested: boolean;
  isInPostSession: boolean;
  showFeedbackForm: boolean;
}

export function isCallSessionLive(state: CallSessionFacts): boolean {
  if (state.endSessionRequested || state.isInPostSession || state.showFeedbackForm) return false;
  return state.giverState === 'ACTIVE' || state.seekerState === 'CONNECTED';
}

/**
 * How long an ending session's way out may take before the sign-out goes on
 * without it: per request (axios) and for the whole leave.
 */
export const SESSION_END_LEAVE_TIMEOUT_MS = 4000;

/**
 * LEAVE THE QUEUE AND END THE CALL FOR A SESSION THAT IS ENDING (David,
 * 2026-10-04: an account change takes the account out of the 1:1 queue).
 *
 * Through the existing ends only: the live call's `endLiveCall` (the partner
 * is told, every device released), `giver/leave-queue`, and `seeker/cancel` or
 * the admin test session's leave, exactly as the flows' own Leave reaches
 * them. Every request is PINNED to `sessionToken`, the outgoing account's: by
 * the time the session-change path runs, the auth store already holds the next
 * account's token (or none), and an unpinned request would leave the queue as
 * the WRONG account. Pinned, its answer is bound to the ended session too, so
 * a 401 for it cannot sign the next account out (api.ts).
 *
 * The set it acts on is read SYNCHRONOUSLY on entry, before the per-user reset
 * that follows it in the same session change (class C: the state measured is
 * the state acted on). Best-effort and bounded: it never throws, it waits at
 * most SESSION_END_LEAVE_TIMEOUT_MS, and a refusal or a timeout is logged
 * rather than swallowed. It writes nothing to the store: the reset does that.
 *
 * Returns whether every request it sent was answered with success (true when
 * there was nothing to leave), so a caller can tell a clean leave from a lost
 * one (class B).
 */
export async function leaveQueueForEndingSession(sessionToken: string): Promise<boolean> {
  const { giverState, seekerState, isTestSession, currentSessionId, audioProvider } =
    useEmpathyQueueStore.getState();
  const pinned = { sessionToken, timeout: SESSION_END_LEAVE_TIMEOUT_MS };

  // The call first, as every in-call ending does: the partner hears the hangup
  // before the queue row goes. `null` still releases a record for a session
  // that was never peer-to-peer, and is a no-op when there is no call.
  endLiveCall(currentSessionId && audioProvider === 'p2p' ? Number(currentSessionId) : null, {
    sessionToken,
  });

  const requests: Array<Promise<unknown>> = [];
  if (giverState !== 'OFFLINE') {
    requests.push(api.post('/empathy-queue/giver/leave-queue', undefined, pinned));
  }
  if (seekerState !== null) {
    requests.push(
      isTestSession && currentSessionId
        ? leaveEmpathyTestSession(Number(currentSessionId), pinned)
        : api.post('/empathy-queue/seeker/cancel', undefined, pinned),
    );
  }
  if (requests.length === 0) return true;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), SESSION_END_LEAVE_TIMEOUT_MS);
  });
  const settled = await Promise.race([Promise.allSettled(requests), timedOut]);
  clearTimeout(timer);

  if (settled === 'timeout') {
    console.warn('[empathy-queue] leave on session end timed out; signing out without it');
    return false;
  }
  const failures = settled.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  for (const failure of failures) {
    console.warn('[empathy-queue] leave on session end failed:', failure.reason);
  }
  return failures.length === 0;
}

// On every session change (#940's one path, auth.store.ts):
//   1. an open feedback form is about the previous account's call: hide it;
//   2. the previous account leaves the queue and its live call ends, under its
//      own token. This listener runs BEFORE the per-user reset below, which is
//      what lets the leave read the queue state it is leaving.
onSessionChange((_next, previous) => {
  useEmpathyQueueStore.setState({
    showFeedbackForm: false,
    feedbackRole: null,
    lastCompletedSessionId: null,
  });
  if (previous) void leaveQueueForEndingSession(previous);
});

// 3. Then the store starts empty for the next account, synchronously, with
//    every per-user store (session-read.ts).
registerUserScopedReset(() => useEmpathyQueueStore.getState().reset());
